'use strict';
/* ============================================================================
 * Lightweight, dependency-free i18n. Translation keys ARE the English source
 * string, so any untranslated string renders in English automatically — new
 * languages drop in by adding a dictionary, with no code changes. Safety-
 * critical permit/library text should be reviewed by a native speaker before
 * a language is offered to clients (Northstar can provide certified localization).
 * ==========================================================================*/
const LANGS = [
  { code: 'en', label: 'English' },
  { code: 'hi', label: 'हिन्दी (Hindi)' },
  { code: 'bn', label: 'বাংলা (Bengali)' },
  { code: 'mr', label: 'मराठी (Marathi)' },
  { code: 'te', label: 'తెలుగు (Telugu)' },
  { code: 'ta', label: 'தமிழ் (Tamil)' },
  { code: 'gu', label: 'ગુજરાતી (Gujarati)' },
  { code: 'kn', label: 'ಕನ್ನಡ (Kannada)' },
  { code: 'pa', label: 'ਪੰਜਾਬੀ (Punjabi)' },
  { code: 'es', label: 'Español (Spanish)' },
  { code: 'fr', label: 'Français (French)' },
  { code: 'ar', label: 'العربية (Arabic)' },
  { code: 'zh', label: '中文 (Chinese)' },
];
const RTL_LANGS = ['ar'];

// Dictionaries map the English source string -> translation for that language.
const DICT = {
  // Hindi SEED — instant paint for nav/common strings; the FULL dictionary
  // (5,400+ strings) now lives in /i18n/dict-hi.json and lazy-loads on demand
  // (P0-11, 22 Aug 2026) — this file no longer carries a ~500KB payload.
  hi: {"Operate":"संचालन","Assess":"मूल्यांकन","People & insight":"लोग और अंतर्दृष्टि","Records & tools":"रिकॉर्ड और उपकरण","Manage":"प्रबंधन","Advisory · plugin":"सलाहकार · प्लगइन","Integrate · plugin":"एकीकरण · प्लगइन","Dashboard":"डैशबोर्ड","Calendar":"कैलेंडर","Incidents":"घटनाएँ","Hazards":"खतरे","CAPA":"सुधारात्मक कार्रवाई","Permits":"कार्य अनुमति","Checklists":"चेकलिस्ट","Maintenance":"रखरखाव","Field safety":"फील्ड सुरक्षा","Audits":"ऑडिट","Risk":"जोखिम","Safety studies":"सुरक्षा अध्ययन","Standards":"मानक","Safe systems":"सुरक्षित प्रणालियाँ","Training":"प्रशिक्षण","Safety culture":"सुरक्षा संस्कृति","Scorecards":"स्कोरकार्ड","Intelligence":"इंटेलिजेंस","ESG":"ईएसजी","Documents":"दस्तावेज़","Assets":"संपत्तियाँ","EHS tools":"ईएचएस उपकरण","Reports":"रिपोर्ट","Expert help":"विशेषज्ञ सहायता","Administration":"प्रशासन","Legal register":"कानूनी रजिस्टर","Settings":"सेटिंग्स","Access control":"पहुँच नियंत्रण","Integrations":"एकीकरण","Cancel":"रद्द करें","Save":"सहेजें","Submit request":"अनुरोध भेजें","Sign in":"साइन इन करें","Search ref or title…":"रेफ़ या शीर्षक खोजें…","Language":"भाषा","Request permit-to-work":"कार्य अनुमति का अनुरोध","Work description":"कार्य विवरण","Permit type":"अनुमति प्रकार","Contractor":"ठेकेदार","Location":"स्थान","Hazards (one per line)":"खतरे (प्रति पंक्ति एक)","Mandatory controls / precautions (one per line)":"अनिवार्य नियंत्रण / सावधानियाँ (प्रति पंक्ति एक)","Required PPE (one per line)":"आवश्यक पीपीई (प्रति पंक्ति एक)","Pre-start checklist":"कार्य-पूर्व चेकलिस्ट","Work description is required":"कार्य विवरण आवश्यक है","Hot Work":"हॉट वर्क (अग्नि कार्य)","Confined Space Entry":"सीमित स्थान प्रवेश","Working at Height":"ऊँचाई पर कार्य","Electrical Work":"विद्युत कार्य","Lockout / Tagout (Isolation)":"लॉकआउट / टैगआउट (पृथक्करण)","Excavation":"खुदाई","Chemical Handling":"रासायनिक हैंडलिंग","General / Cold Work":"सामान्य / कोल्ड वर्क","days":"दिन","hours":"घंटे","ago":"पहले","day":"दिन","hour":"घंटा"},
  es: {
    'Operate': 'Operar', 'Assess': 'Evaluar', 'People & insight': 'Personas e información', 'Records & tools': 'Registros y herramientas', 'Manage': 'Gestionar',
    'Dashboard': 'Panel', 'Calendar': 'Calendario', 'Incidents': 'Incidentes', 'Hazards': 'Peligros', 'CAPA': 'CAPA', 'Permits': 'Permisos', 'Checklists': 'Listas de control',
    'Maintenance': 'Mantenimiento', 'Field safety': 'Seguridad en campo', 'Audits': 'Auditorías', 'Risk': 'Riesgo', 'Safety studies': 'Estudios de seguridad', 'Standards': 'Normas',
    'Safe systems': 'Sistemas seguros', 'Training': 'Capacitación', 'Safety culture': 'Cultura de seguridad', 'Scorecards': 'Cuadros de mando', 'Intelligence': 'Inteligencia', 'ESG': 'ESG',
    'Documents': 'Documentos', 'Assets': 'Activos', 'EHS tools': 'Herramientas EHS', 'Reports': 'Informes', 'Expert help': 'Ayuda experta', 'Administration': 'Administración', 'Legal register': 'Registro legal', 'Settings': 'Ajustes', 'Access control': 'Control de acceso',
    'Cancel': 'Cancelar', 'Save': 'Guardar', 'Submit request': 'Enviar solicitud', 'Sign in': 'Iniciar sesión', 'Language': 'Idioma',
    'Request permit-to-work': 'Solicitar permiso de trabajo', 'Work description': 'Descripción del trabajo', 'Permit type': 'Tipo de permiso', 'Contractor': 'Contratista', 'Location': 'Ubicación',
    'Pre-start checklist': 'Lista previa al inicio', 'Hot Work': 'Trabajo en caliente', 'Confined Space Entry': 'Entrada a espacio confinado', 'Working at Height': 'Trabajo en altura', 'Electrical Work': 'Trabajo eléctrico',
    'Lockout / Tagout (Isolation)': 'Bloqueo / Etiquetado', 'Excavation': 'Excavación', 'Chemical Handling': 'Manejo de químicos', 'General / Cold Work': 'General / Trabajo en frío',
  },
  fr: {
    'Operate': 'Exploiter', 'Assess': 'Évaluer', 'People & insight': 'Personnes et analyses', 'Records & tools': 'Dossiers et outils', 'Manage': 'Gérer',
    'Dashboard': 'Tableau de bord', 'Calendar': 'Calendrier', 'Incidents': 'Incidents', 'Hazards': 'Dangers', 'CAPA': 'CAPA', 'Permits': 'Permis', 'Checklists': 'Listes de contrôle',
    'Maintenance': 'Maintenance', 'Field safety': 'Sécurité terrain', 'Audits': 'Audits', 'Risk': 'Risque', 'Safety studies': 'Études de sécurité', 'Standards': 'Normes',
    'Safe systems': 'Systèmes sûrs', 'Training': 'Formation', 'Safety culture': 'Culture sécurité', 'Scorecards': 'Tableaux de bord', 'Intelligence': 'Intelligence', 'ESG': 'ESG',
    'Documents': 'Documents', 'Assets': 'Actifs', 'EHS tools': 'Outils EHS', 'Reports': 'Rapports', 'Expert help': 'Aide experte', 'Administration': 'Administration', 'Legal register': 'Registre légal', 'Settings': 'Paramètres', 'Access control': 'Contrôle d’accès',
    'Cancel': 'Annuler', 'Save': 'Enregistrer', 'Submit request': 'Envoyer la demande', 'Sign in': 'Se connecter', 'Language': 'Langue',
    'Request permit-to-work': 'Demander un permis de travail', 'Work description': 'Description du travail', 'Permit type': 'Type de permis', 'Contractor': 'Sous-traitant', 'Location': 'Lieu',
    'Pre-start checklist': 'Liste avant démarrage', 'Hot Work': 'Travail à chaud', 'Confined Space Entry': 'Entrée espace confiné', 'Working at Height': 'Travail en hauteur', 'Electrical Work': 'Travaux électriques',
    'Lockout / Tagout (Isolation)': 'Consignation (LOTO)', 'Excavation': 'Excavation', 'Chemical Handling': 'Manipulation chimique', 'General / Cold Work': 'Général / Travail à froid',
  },
  bn: {
    'Operate': 'পরিচালনা', 'Assess': 'মূল্যায়ন', 'People & insight': 'মানুষ ও অন্তর্দৃষ্টি', 'Records & tools': 'রেকর্ড ও সরঞ্জাম', 'Manage': 'পরিচালন',
    'Dashboard': 'ড্যাশবোর্ড', 'Calendar': 'ক্যালেন্ডার', 'Incidents': 'ঘটনা', 'Hazards': 'বিপদ', 'Permits': 'কার্য অনুমতি', 'Checklists': 'চেকলিস্ট', 'Maintenance': 'রক্ষণাবেক্ষণ',
    'Field safety': 'ফিল্ড নিরাপত্তা', 'Audits': 'অডিট', 'Risk': 'ঝুঁকি', 'Training': 'প্রশিক্ষণ', 'Reports': 'প্রতিবেদন', 'Settings': 'সেটিংস', 'Cancel': 'বাতিল', 'Sign in': 'সাইন ইন', 'Language': 'ভাষা',
    'Request permit-to-work': 'কার্য অনুমতির অনুরোধ', 'Work description': 'কাজের বিবরণ', 'Permit type': 'অনুমতির ধরন', 'Pre-start checklist': 'শুরুর আগের চেকলিস্ট', 'Hot Work': 'হট ওয়ার্ক', 'Confined Space Entry': 'সীমাবদ্ধ স্থান প্রবেশ', 'Working at Height': 'উচ্চতায় কাজ',
  },
  mr: {
    'Operate': 'संचालन', 'Assess': 'मूल्यांकन', 'Manage': 'व्यवस्थापन', 'Dashboard': 'डॅशबोर्ड', 'Incidents': 'घटना', 'Hazards': 'धोके', 'Permits': 'कार्य परवाना',
    'Checklists': 'चेकलिस्ट', 'Maintenance': 'देखभाल', 'Audits': 'ऑडिट', 'Risk': 'जोखीम', 'Training': 'प्रशिक्षण', 'Reports': 'अहवाल', 'Settings': 'सेटिंग्ज', 'Cancel': 'रद्द करा', 'Sign in': 'साइन इन', 'Language': 'भाषा',
    'Request permit-to-work': 'कार्य परवान्याची विनंती', 'Work description': 'कामाचे वर्णन', 'Permit type': 'परवाना प्रकार', 'Pre-start checklist': 'सुरुवातीपूर्व चेकलिस्ट', 'Hot Work': 'हॉट वर्क', 'Working at Height': 'उंचीवर काम',
  },
  ta: {
    'Operate': 'செயல்பாடு', 'Manage': 'நிர்வாகம்', 'Dashboard': 'டாஷ்போர்டு', 'Incidents': 'சம்பவங்கள்', 'Hazards': 'அபாயங்கள்', 'Permits': 'பணி அனுமதி',
    'Checklists': 'சரிபார்ப்புப் பட்டியல்', 'Maintenance': 'பராமரிப்பு', 'Audits': 'தணிக்கைகள்', 'Risk': 'ஆபத்து', 'Training': 'பயிற்சி', 'Reports': 'அறிக்கைகள்', 'Settings': 'அமைப்புகள்', 'Cancel': 'ரத்து', 'Sign in': 'உள்நுழை', 'Language': 'மொழி',
    'Request permit-to-work': 'பணி அனுமதி கோரிக்கை', 'Work description': 'பணி விவரம்', 'Permit type': 'அனுமதி வகை', 'Pre-start checklist': 'தொடக்கத்திற்கு முந்தைய சரிபார்ப்பு', 'Hot Work': 'வெப்பப் பணி', 'Working at Height': 'உயரத்தில் பணி',
  },
  te: {
    'Operate': 'నిర్వహణ', 'Manage': 'నిర్వహణ', 'Dashboard': 'డాష్‌బోర్డ్', 'Incidents': 'సంఘటనలు', 'Hazards': 'ప్రమాదాలు', 'Permits': 'పని అనుమతి',
    'Checklists': 'చెక్‌లిస్ట్', 'Maintenance': 'నిర్వహణ', 'Audits': 'ఆడిట్‌లు', 'Risk': 'ప్రమాదం', 'Training': 'శిక్షణ', 'Reports': 'నివేదికలు', 'Settings': 'సెట్టింగ్‌లు', 'Cancel': 'రద్దు', 'Sign in': 'సైన్ ఇన్', 'Language': 'భాష',
    'Request permit-to-work': 'పని అనుమతి అభ్యర్థన', 'Work description': 'పని వివరణ', 'Permit type': 'అనుమతి రకం', 'Pre-start checklist': 'ప్రారంభానికి ముందు చెక్‌లిస్ట్', 'Hot Work': 'హాట్ వర్క్', 'Working at Height': 'ఎత్తులో పని',
  },
  gu: {
    'Operate': 'સંચાલન', 'Manage': 'વ્યવસ્થાપન', 'Dashboard': 'ડેશબોર્ડ', 'Incidents': 'ઘટનાઓ', 'Hazards': 'જોખમો', 'Permits': 'કાર્ય પરવાનગી',
    'Checklists': 'ચેકલિસ્ટ', 'Maintenance': 'જાળવણી', 'Audits': 'ઓડિટ', 'Risk': 'જોખમ', 'Training': 'તાલીમ', 'Reports': 'અહેવાલો', 'Settings': 'સેટિંગ્સ', 'Cancel': 'રદ કરો', 'Sign in': 'સાઇન ઇન', 'Language': 'ભાષા',
    'Request permit-to-work': 'કાર્ય પરવાનગીની વિનંતી', 'Work description': 'કાર્ય વર્ણન', 'Permit type': 'પરવાનગીનો પ્રકાર', 'Pre-start checklist': 'શરૂઆત પહેલાંની ચેકલિસ્ટ', 'Hot Work': 'હોટ વર્ક', 'Working at Height': 'ઊંચાઈ પર કાર્ય',
  },
  kn: {
    'Operate': 'ಕಾರ್ಯಾಚರಣೆ', 'Manage': 'ನಿರ್ವಹಣೆ', 'Dashboard': 'ಡ್ಯಾಶ್‌ಬೋರ್ಡ್', 'Incidents': 'ಘಟನೆಗಳು', 'Hazards': 'ಅಪಾಯಗಳು', 'Permits': 'ಕೆಲಸದ ಅನುಮತಿ',
    'Checklists': 'ಪರಿಶೀಲನಾಪಟ್ಟಿ', 'Maintenance': 'ನಿರ್ವಹಣೆ', 'Audits': 'ಲೆಕ್ಕಪರಿಶೋಧನೆ', 'Risk': 'ಅಪಾಯ', 'Training': 'ತರಬೇತಿ', 'Reports': 'ವರದಿಗಳು', 'Settings': 'ಸೆಟ್ಟಿಂಗ್‌ಗಳು', 'Cancel': 'ರದ್ದುಮಾಡಿ', 'Sign in': 'ಸೈನ್ ಇನ್', 'Language': 'ಭಾಷೆ',
    'Request permit-to-work': 'ಕೆಲಸದ ಅನುಮತಿ ವಿನಂತಿ', 'Work description': 'ಕೆಲಸದ ವಿವರಣೆ', 'Permit type': 'ಅನುಮತಿ ಪ್ರಕಾರ', 'Pre-start checklist': 'ಪ್ರಾರಂಭದ ಮೊದಲಿನ ಪಟ್ಟಿ', 'Hot Work': 'ಹಾಟ್ ವರ್ಕ್', 'Working at Height': 'ಎತ್ತರದಲ್ಲಿ ಕೆಲಸ',
  },
  pa: {
    'Operate': 'ਸੰਚਾਲਨ', 'Manage': 'ਪ੍ਰਬੰਧਨ', 'Dashboard': 'ਡੈਸ਼ਬੋਰਡ', 'Incidents': 'ਘਟਨਾਵਾਂ', 'Hazards': 'ਖ਼ਤਰੇ', 'Permits': 'ਕੰਮ ਪਰਮਿਟ',
    'Checklists': 'ਚੈੱਕਲਿਸਟ', 'Maintenance': 'ਰੱਖ-ਰਖਾਅ', 'Audits': 'ਆਡਿਟ', 'Risk': 'ਜੋਖਮ', 'Training': 'ਸਿਖਲਾਈ', 'Reports': 'ਰਿਪੋਰਟਾਂ', 'Settings': 'ਸੈਟਿੰਗਾਂ', 'Cancel': 'ਰੱਦ ਕਰੋ', 'Sign in': 'ਸਾਈਨ ਇਨ', 'Language': 'ਭਾਸ਼ਾ',
    'Request permit-to-work': 'ਕੰਮ ਪਰਮਿਟ ਦੀ ਬੇਨਤੀ', 'Work description': 'ਕੰਮ ਦਾ ਵੇਰਵਾ', 'Permit type': 'ਪਰਮਿਟ ਕਿਸਮ', 'Pre-start checklist': 'ਸ਼ੁਰੂ ਤੋਂ ਪਹਿਲਾਂ ਚੈੱਕਲਿਸਟ', 'Hot Work': 'ਹਾਟ ਵਰਕ', 'Working at Height': 'ਉਚਾਈ ਤੇ ਕੰਮ',
  },
  ar: {
    'Operate': 'التشغيل', 'Assess': 'التقييم', 'Manage': 'الإدارة', 'Dashboard': 'لوحة التحكم', 'Incidents': 'الحوادث', 'Hazards': 'المخاطر', 'Permits': 'تصاريح العمل',
    'Checklists': 'قوائم التحقق', 'Maintenance': 'الصيانة', 'Audits': 'عمليات التدقيق', 'Risk': 'المخاطر', 'Training': 'التدريب', 'Reports': 'التقارير', 'Settings': 'الإعدادات', 'Cancel': 'إلغاء', 'Sign in': 'تسجيل الدخول', 'Language': 'اللغة',
    'Request permit-to-work': 'طلب تصريح عمل', 'Work description': 'وصف العمل', 'Permit type': 'نوع التصريح', 'Pre-start checklist': 'قائمة ما قبل البدء', 'Hot Work': 'العمل الساخن', 'Working at Height': 'العمل على ارتفاع', 'Confined Space Entry': 'الدخول إلى مكان محصور',
  },
  zh: {
    'Operate': '运营', 'Assess': '评估', 'Manage': '管理', 'Dashboard': '仪表板', 'Incidents': '事件', 'Hazards': '危险源', 'Permits': '作业许可',
    'Checklists': '检查表', 'Maintenance': '维护', 'Audits': '审核', 'Risk': '风险', 'Training': '培训', 'Reports': '报告', 'Settings': '设置', 'Cancel': '取消', 'Sign in': '登录', 'Language': '语言',
    'Request permit-to-work': '申请作业许可', 'Work description': '作业描述', 'Permit type': '许可类型', 'Pre-start checklist': '开工前检查表', 'Hot Work': '动火作业', 'Confined Space Entry': '受限空间进入', 'Working at Height': '高处作业',
  },
};

// Broad coverage of common UI strings (Hindi primary). The DOM-translation pass
// below applies these everywhere, so views need not wrap every string in t().

/* Phase 2 — expanded Hindi coverage (UI chrome + field/permit/incident/hazard).
   Keys are the exact English source strings; drafts for Northstar review.
   Any string still in English is captured by window.__i18nMissing (coverage audit). */


/* Phase 2b — contractor/field + safety-ops Hindi (drafts for Northstar review). */


/* Phase 3 — admin/reporting/dashboard/onboarding Hindi (drafts for Northstar review). */


/* Phase 4 — first-screen (nav/overview/attention/setup) + common form labels (drafts for Northstar review). */


/* Phase 4b — trial banner static fragments (number stays; rest translates). */


/* Phase 5 — deep-tail empty-states + module labels (drafts for Northstar review). */



// (hi time-token keys folded into the seed + packaged dictionary — P0-11)







let LANG = (typeof localStorage !== 'undefined' && localStorage.getItem('esos_lang')) || 'en';

/* ---- Packaged full dictionaries (P0-11, 22 Aug 2026) --------------------------
 * Full authored dictionaries live as static packs at /i18n/dict-<lang>.json and
 * lazy-load ONCE when their language is activated — the app bundle stays small
 * (Hindi alone was ~500KB inline before this) and every packaged language gets
 * the same depth. The inline DICT entries above are instant-paint seeds; the
 * pack merges over them, then the whole DOM re-translates. Anything still
 * missing falls back to English (and the /api/i18n/translate MT layer). */
const PACKAGED_DICTS = ['hi', 'bn', 'mr', 'ta', 'te', 'gu', 'kn', 'pa'];
const DICT_VERSION = '2026-08-22-1'; // bump when packs regenerate (busts the SW cache)
const PACK_LOADED = {};
function loadPackagedDict(code) {
  if (typeof fetch !== 'function' || !PACKAGED_DICTS.includes(code) || PACK_LOADED[code]) return Promise.resolve(false);
  PACK_LOADED[code] = true; // set eagerly so concurrent switches don't double-fetch
  return fetch('/i18n/dict-' + code + '.json?v=' + DICT_VERSION, { credentials: 'same-origin' })
    .then((r) => (r.ok ? r.json() : null))
    .then((pack) => {
      if (!pack) { PACK_LOADED[code] = false; return false; }
      DICT[code] = Object.assign({}, DICT[code] || {}, pack);
      if (LANG === code && typeof document !== 'undefined' && typeof translateDOM === 'function') translateDOM(document.body);
      return true;
    })
    .catch(() => { PACK_LOADED[code] = false; return false; });
}

/** Translate an English source string to the active language (falls back to English). */
function t(key) { const d = DICT[LANG]; return (d && d[key]) || key; }
function curLang() { return LANG; }
function setLang(code) {
  LANG = DICT[code] || code === 'en' ? code : 'en';
  try { localStorage.setItem('esos_lang', LANG); } catch (_) {}
  document.documentElement.lang = LANG;
  document.documentElement.dir = RTL_LANGS.includes(LANG) ? 'rtl' : 'ltr';
  loadPackagedDict(LANG);
}
/** Seed the active language from the signed-in user's saved preference, then the
 *  tenant default, then any device choice. Call once on app entry (before first render). */
function initUserLang(user, tenant) {
  var code = (user && user.lang) || (tenant && tenant.defaultLang) || (typeof localStorage !== 'undefined' && localStorage.getItem('esos_lang')) || 'en';
  if (code && code !== LANG) setLang(code);
  else loadPackagedDict(LANG); // stored language unchanged since boot — still fetch its pack
  if (LANG !== 'en' && typeof startI18nObserver === 'function') startI18nObserver();
  return LANG;
}

/**
 * Translate the leading phrase of a COMPOSED detail string of the form
 * "phrase — data", "phrase (data)" or "phrase 5d …", leaving the trailing data
 * (legal citations, names, counts) in place. Used for Attention row details
 * where a fixed English phrase is glued to variable data a whole-string
 * dictionary match can never catch. Falls back to a whole-string match, then to
 * the original string unchanged.
 */
function tLead(s) {
  if (!s) return s;
  if (LANG === 'en') return String(s);
  const dict = DICT[LANG]; if (!dict) return String(s);
  if (dict[s] !== undefined) return dict[s];
  const str = String(s);
  const m = str.match(/^(.*?)(\s+(?:[—\-(·:]|\d).*)$/);
  if (m) { const lead = m[1].trim(); if (lead && dict[lead] !== undefined) return dict[lead] + m[2]; }
  return str;
}

/**
 * Full localisation of a composed Attention/list DETAIL string: translate the
 * leading phrase (via tLead) AND the residual inline time tokens that carry
 * dynamic numbers — "5d" → "5 दिन", "3h" → "3 घंटे", "ago" → "पहले" — so no
 * English fragment is left dangling next to a legal citation or a name. Token
 * words come from the dictionary, so this works for any language, not just hi.
 * Scoped to detail strings only (numbers followed by a d/h boundary reliably
 * mean days/hours in these feeds).
 */
function localizeDetail(s) {
  var out = tLead(s);
  if (LANG === 'en' || !out) return out;
  var dict = DICT[LANG] || {};
  var days = dict['days'] || 'days', hours = dict['hours'] || 'hours', ago = dict['ago'] || 'ago';
  return String(out)
    .replace(/(\d+)\s*d\b/g, '$1 ' + days)
    .replace(/(\d+)\s*h\b/g, '$1 ' + hours)
    .replace(/\bago\b/g, ago);
}

/**
 * Dict-only translation of a single node/subtree (NO machine-translation layer).
 * Idempotent by construction: once a text node is rewritten to the target
 * language its trimmed value is no longer an English dictionary key, so a second
 * pass is a no-op — which is what makes the MutationObserver below safe from
 * infinite loops. SCRIPT/STYLE/TEXTAREA text is skipped so page code and raw
 * user input are never rewritten.
 */
function translateSubtreeLocal(root) {
  if (LANG === 'en' || !root || typeof document === 'undefined') return;
  const dict = DICT[LANG]; if (!dict) return;
  const SKIP = { SCRIPT: 1, STYLE: 1, TEXTAREA: 1, NOSCRIPT: 1, CODE: 1, PRE: 1 };
  const swap = (tn) => {
    const raw = tn.nodeValue; if (!raw) return; const key = raw.trim(); if (!key) return;
    const pn = tn.parentNode && tn.parentNode.nodeName; if (pn && SKIP[pn]) return;
    const tr = dict[key]; if (tr !== undefined && tr !== key) tn.nodeValue = raw.replace(key, tr);
  };
  try {
    if (root.nodeType === 3) { swap(root); return; }
    if (root.nodeType !== 1) return;
    const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT, null);
    const nodes = []; let n; while ((n = walker.nextNode())) nodes.push(n);
    for (const tn of nodes) swap(tn);
    if (root.querySelectorAll) root.querySelectorAll('[placeholder],[title],[aria-label]').forEach((el) => {
      ['placeholder', 'title', 'aria-label'].forEach((attr) => { const v = el.getAttribute(attr); if (v && dict[v.trim()] !== undefined && dict[v.trim()] !== v.trim()) el.setAttribute(attr, dict[v.trim()]); });
    });
  } catch (_) {}
}

/**
 * Global re-translation safety net. The main content (#app) is translated by
 * render()→translateDOM, but the shell chrome — breadcrumbs, the sidebar
 * Recent/Favourites lists, the trial / verify / demo banners and the topbar —
 * is injected by code paths that set innerHTML directly and never call
 * translateDOM. This observer keeps every one of them (and any future
 * dynamically-rendered content) translated automatically, so the UI can't drift
 * back into a half-English state. Writes are batched on the next animation frame
 * to coalesce bursts, and the idempotency of translateSubtreeLocal guarantees no
 * feedback loop from our own text-node edits.
 */
var _i18nObserver = null, _i18nPending = null;
function startI18nObserver() {
  if (_i18nObserver || typeof MutationObserver === 'undefined' || typeof document === 'undefined') return;
  const target = document.body; if (!target) return;
  const queue = new Set();
  const flush = () => {
    _i18nPending = null;
    if (LANG === 'en') { queue.clear(); return; }
    const batch = [...queue]; queue.clear();
    for (const node of batch) { if (node && node.isConnected !== false) translateSubtreeLocal(node); }
  };
  const schedule = () => { if (queue.size && !_i18nPending) { _i18nPending = (typeof requestAnimationFrame === 'function') ? requestAnimationFrame(flush) : setTimeout(flush, 16); } };
  _i18nObserver = new MutationObserver((muts) => {
    if (LANG === 'en') return;
    for (const m of muts) {
      if (m.type === 'characterData') queue.add(m.target);
      else if (m.type === 'childList' && m.addedNodes) m.addedNodes.forEach((nd) => queue.add(nd));
    }
    schedule();
  });
  _i18nObserver.observe(target, { childList: true, subtree: true, characterData: true });
  // Immediate sweep of whatever shell chrome already exists at hook-up time.
  translateSubtreeLocal(target);
}
/** Re-apply language across nav, topbar and the current view. */
function applyLanguage(code) {
  setLang(code);
  if (typeof UI !== 'undefined' && UI.Theme) UI.Theme.applyDir(code);
  if (typeof buildNav === 'function') buildNav();
  if (typeof setupTopbar === 'function') setupTopbar();
  if (typeof renderTrialBanner === 'function') renderTrialBanner();
  if (typeof router === 'function') router();
  if (code !== 'en') {
    if (typeof startI18nObserver === 'function') startI18nObserver();
    if (typeof translateSubtreeLocal === 'function' && typeof document !== 'undefined') translateSubtreeLocal(document.body);
  }
}
/** Translate all dictionary-known text within a rendered subtree (any view, any
 *  modal) — so a language change applies everywhere, not just the menu. */
function translateDOM(root) {
  if (LANG === 'en' || !root || typeof document === 'undefined') return;
  const dict = DICT[LANG]; if (!dict) return;
  try {
    const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT, null);
    const nodes = []; let n; while ((n = walker.nextNode())) nodes.push(n);
    for (const tn of nodes) {
      const raw = tn.nodeValue; if (!raw) continue; const key = raw.trim(); if (!key) continue;
      const tr = dict[key]; if (tr !== undefined && tr !== key) tn.nodeValue = raw.replace(key, tr);
    }
    root.querySelectorAll('[placeholder],[title],[aria-label]').forEach((el) => {
      ['placeholder', 'title', 'aria-label'].forEach((attr) => { const v = el.getAttribute(attr); if (v && dict[v.trim()] !== undefined) el.setAttribute(attr, dict[v.trim()]); });
    });
  } catch (_) {}
  // Fill in anything the local dictionary didn't cover via the cached MT layer.
  mtTranslateDOM(root);
}

/* ----------------------------------------------------------------------------
 * Machine-translation fallback. Collects every visible string the local
 * dictionary doesn't have, asks the backend (which caches + translates once),
 * merges results into DICT[LANG], then re-applies. Whole-UI localization with
 * no per-string hand-coding. Gracefully no-ops if offline / provider disabled.
 * --------------------------------------------------------------------------*/
const MT_REQUESTED = new Set();   // `${lang} ${src}` already asked for (avoid refetch)
function _mtTranslatable(s) {
  return !!s && s.length <= 200 && /[A-Za-z]/.test(s) && !/^[\d\s.,:%/()–—-]+$/.test(s);
}
async function mtTranslateDOM(root) {
  if (LANG === 'en' || !root || typeof document === 'undefined' || typeof fetch !== 'function') return;
  const dict = (DICT[LANG] = DICT[LANG] || {});
  const want = new Set();
  try {
    const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT, null);
    let n; while ((n = walker.nextNode())) { const k = (n.nodeValue || '').trim(); if (_mtTranslatable(k) && dict[k] === undefined) want.add(k); }
    root.querySelectorAll('[placeholder],[title],[aria-label]').forEach((el) => {
      ['placeholder', 'title', 'aria-label'].forEach((attr) => { const v = el.getAttribute(attr); const k = v && v.trim(); if (_mtTranslatable(k) && dict[k] === undefined) want.add(k); });
    });
  } catch (_) { return; }
  // Coverage audit: record every visible string with no local translation so
  // localisation can be driven to complete. Inspect via window.__i18nMissing.
  if (typeof window !== 'undefined' && want.size) { window.__i18nMissing = window.__i18nMissing || new Set(); want.forEach((k) => window.__i18nMissing.add(k)); }
  const list = [...want].filter((k) => !MT_REQUESTED.has(LANG + ' ' + k)).slice(0, 80);
  if (!list.length) return;
  list.forEach((k) => MT_REQUESTED.add(LANG + ' ' + k));
  try {
    const res = await fetch('/api/i18n/translate', { method: 'POST', credentials: 'same-origin', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ lang: LANG, strings: list }) });
    if (!res.ok) return;
    const data = await res.json();
    const tr = data && data.translations; if (!tr) return;
    let any = false;
    for (const k in tr) { if (tr[k] && tr[k] !== k && dict[k] === undefined) { dict[k] = tr[k]; any = true; } }
    if (any) translateDOM(root); // re-apply (will no-op MT since now cached locally)
  } catch (_) {}
}

// initialise document direction on load
if (typeof document !== 'undefined') { document.documentElement.lang = LANG; document.documentElement.dir = RTL_LANGS.includes(LANG) ? 'rtl' : 'ltr'; }
